import {
    Form,
    Modal,
    Input,
    notification,
    Select,
    Alert,
    Spin,
    Space,
    message,
    Card,
    Typography,
} from 'antd';
import {
    useCallback,
    useContext, useEffect, useRef, useState,
} from 'react';
import { debounce } from 'lodash';
import { addInvoiceTransaction } from '../../../api/dispersions';
import { InvoicesContext } from '../../Invoices/context/invoices.context';
import { IInvoice } from '../../Invoices/context/invoices.interface';
import { getBookings } from '../../../api/booking';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;
interface IProps {
    invoice: IInvoice
    visible: boolean;
    allTransactions: any;
    onCancel: (transaction: any) => void;
}

export const AddTransactionModal = ({
    invoice,
    visible,
    allTransactions,
    onCancel,
}: IProps) => {
    const { fetchData } = useContext(InvoicesContext);

    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [amountValidation, setAmountValidation] = useState<{ loading: boolean, warning?: string, info?: string }>({ loading: false });
    const [bookings, setBookings] = useState<any[]>([]);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const amount = Form.useWatch('amount', form);

    const validateAmount = () => {
        setAmountValidation({ loading: true });
        const amount = form.getFieldValue('amount') as number;

        const result = Number(invoice.amount) + Number(amount);

        const formatCurrency = (value: number) => {
            const absValue = Math.abs(value);
            const formatted = `${absValue.toFixed(2)}`;
            return value < 0 ? `- ${formatted}` : formatted;
        };

        const sign = amount > 0 ? '+' : '';
        const infoMessage = `${formatCurrency(invoice.amount)} ${sign} ${formatCurrency(amount)} = ${formatCurrency(result)} ${invoice.currency}.`;

        if (result <= 0) {
            const warningMessage = 'The compensation would leave the invoice total at 0 or less';
            setAmountValidation({ loading: false, warning: warningMessage, info: infoMessage });
            return;
        }

        setAmountValidation({ loading: false, info: infoMessage });
    };

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (amount !== undefined && amount !== null && amount !== 0) {
            debounceTimerRef.current = setTimeout(() => {
                validateAmount();
            }, 250);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [amount]);

    const onFinish = (values: any) => form.validateFields().then((values) => {
        setLoading(true);

        addInvoiceTransaction(invoice._id, values)
            .then(({ data }) => {
                notification.success({
                    message: 'Transaction added successfully',
                });
                fetchData();
                onCancel(data.data);
            })
            .catch((error) => {
                const description = error.response?.data?.message;
                notification.error({
                    message: 'Error adding transaction',
                    description,
                });
            })
            .finally(() => {
                setLoading(false);
                form.resetFields();
            });
    });

    const handleSearchBooking = (value: string) => {
        setBookings([]);

        if (!value) {
            setBookings([]);
            return;
        }

        if (value && value.length < 3) {
            message.warning('Please enter at least 3 characters');
            return;
        }

        setLoading(true);

        const query: any = {
            limit: 10,
            page: 1,
            searchBooking: value,
        };

        getBookings(query)
            .then(({ data }: any) => {
                const { data: bookingData } = data;
                setBookings(bookingData.data);
            })
            .catch((error) => {
                const description = error.response?.data?.message;
                notification.error({
                    message: 'Error searching booking',
                    description,
                });
                setBookings([]);
            })
            .finally(() => setLoading(false));
    };

    const handleSearchBookingDebounced = useCallback(
        debounce((value: string) => {
            handleSearchBooking(value);
        }, 700),
        [],
    );

    return (
        <Modal
            open={visible}
            centered
            title="Add new compensation"
            onCancel={() => onCancel(null)}
            onOk={() => form.submit()}
            okButtonProps={{
                disabled: loading || Boolean(amountValidation.warning),
            }}
            okText="Add"
            confirmLoading={loading}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {
                    amountValidation.loading && <Spin />
                }
                {
                    amountValidation.warning && (
                        <Alert message={amountValidation.warning} type="warning" />
                    )
                }
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[
                            {
                                required: true,
                                message: 'Please select a type!',
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select a type"
                        >
                            <Option value="COMPENSATION">Compensación</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="bookingNumericId"
                        label="Booking (optional)"
                        help="You can set booking if you want to pay for a specific booking"
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select a booking"
                            onSearch={handleSearchBookingDebounced}
                            loading={loading}
                            notFoundContent={loading ? <Spin /> : null}
                        >
                            {
                                bookings.length > 0 && bookings.map((item: any) => (
                                    <Option
                                        key={item._id}
                                        value={item.bookingNumericId}
                                    >
                                        {`Nº ${item.bookingNumericId} - ${item.statusText}`}
                                    </Option>
                                ))
                            }
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[
                            {
                                required: true,
                                message: 'Please input amount!',
                            },
                        ]}
                    >
                        <Input
                            type="number"
                        />
                    </Form.Item>
                    <Form.Item
                        name="comment"
                        label="Comment"
                    >
                        {/* <Select
                            options={options}
                            placeholder="Select a reason"
                        /> */}
                        <TextArea rows={4} />
                    </Form.Item>

                    <Card style={{ width: '100%', marginTop: 16 }} bodyStyle={{ padding: '12px 16px' }}>
                        <Space size={8} direction="vertical">
                            <Text type="secondary">Result:</Text>
                            {
                                amountValidation.loading ? <Spin /> : (
                                    <Text strong>{amountValidation.info}</Text>
                                )
                            }
                        </Space>
                    </Card>
                </Form>
            </Space>
        </Modal>
    );
};
